import { AddCircleOutline, Delete, Edit } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { Checklist} from '../../../generated-types/checklist-types'
import { useGetChecklists, useDeleteChecklist } from 'src/api/useChecklist'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import GenericTable from 'src/components/table/GenericTable'
import { useDialogCtx, useCustomerCtx } from 'src/hooks/context-hooks'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { AppRoutesNavigation } from 'src/app-routes'

const ChecklistsPage = () => {
    const { customer } = useCustomerCtx()
    const customerId = customer?.id as number
    const { showConfirmDialog } = useDialogCtx()
    const { t } = useTranslation(['checklist', 'common'])
    const theme = useTheme()
    const navigate = useNavigate()

    const { data, isLoading } = useGetChecklists(customerId)
    const deleteMutation = useDeleteChecklist(customerId)

    const handleDelete = (checklist: Checklist) => {
        showConfirmDialog(
            t('delete-checklist-title'),
            t('delete-checklist-content'),
            {
                acceptText: t('yes', { ns: 'common' }),
                cancelText: t('no', { ns: 'common' }),
            },
            async pop => {
                await deleteMutation.mutateAsync(checklist.id, {
                    onSuccess: () => {
                        toast.success(t('checklist-deleted'))
                        pop()
                    },
                    onError: () => {
                        toast.error(t('error-deleting-checklist'))
                    },
                })
            },
        )
    }

    const columns = [
        {
            field: 'name',
            headerName: t('name', { ns: 'common' }),
            flex: 3,
        },
        {
            field: 'description',
            headerName: t('description', { ns: 'common' }),
            flex: 4,
            renderCell: params => params.row.description || '-',
        },
        {
            field: 'items',
            headerName: t('items'),
            flex: 1,
            renderCell: params => params.row.items?.length ?? 0,
        },
        {
            field: 'actions',
            headerName: t('actions', { ns: 'common' }),
            flex: 2,
            renderCell: params => (
                <Box width={200} display="flex" flexDirection="row" justifyContent="space-evenly">
                    <Edit
                        className="cursor-pointer"
                        onClick={() => navigate(AppRoutesNavigation.Customer.editChecklist(customerId, params.row.id))}
                    />
                    <Delete
                        className="cursor-pointer"
                        onClick={() => handleDelete(params.row as Checklist)}
                    />
                </Box>
            ),
        },
    ]

    return (
        <CommonPageLayout
            titleSection={{ title: t('checklists') }}
            topAction={[
                {
                    buttonText: t('new-checklist'),
                    buttonIcon: <AddCircleOutline />,
                    onClick: () => navigate(AppRoutesNavigation.Customer.createChecklist(customerId)),
                    variant: 'outlined',
                    shadowWeight: null,
                    border: 0,
                },
            ]}
        >
            <Box padding="8px" bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius="4px">
                <GenericTable
                    id="checklists"
                    columns={columns}
                    data={data || []}
                    autoHeight={false}
                    height="81vh"
                    loading={isLoading}
                />
            </Box>
        </CommonPageLayout>
    )
}

export default ChecklistsPage